// Load one API resource with real loading / error states, and reload on demand. The previous
// value stays on screen while reloading, so lists do not flash empty.
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, errorText } from './api';

export interface Loaded<T> {
  data: T | undefined;
  error: string | null;
  loading: boolean;
  reload: () => Promise<void>;
  setData: (v: T) => void;
}

export function useApi<T>(path: string | null): Loaded<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(path));
  const current = useRef(path);
  current.current = path;

  const reload = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try {
      const v = await api<T>(path);
      if (current.current === path) {
        setData(v);
        setError(null);
      }
    } catch (e) {
      if (current.current === path) setError(errorText(e));
    } finally {
      if (current.current === path) setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    setData(undefined);
    void reload();
  }, [reload]);

  return { data, error, loading, reload, setData };
}
